import { useRef } from 'react';
import { motion, useReducedMotion, useScroll } from 'framer-motion';
import { ArrowRightIcon, StarIcon } from 'lucide-react';
import { MediaView } from '../shared/MediaView';
import { HeroDoors } from './HeroDoors';
import { founder, pressLogos } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { usePages } from '../../contexts/SiteContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useCompactMotion, useLayerValue, useLayerY } from '../../hooks/useParallax';
import { ease } from '../../core/format/motion';
import { plural } from '../../core/format/text';
import { mediaOr } from '../../core/rules/media';
import { placeholderImages } from '../../data/placeholderImages';
import { fill } from '../../utils/copy';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';
import { pageActions } from '../../data/pageActions';
import { homeAnchor } from '../../data/homeSections';
type Props = {
  reviewCount: number;
  avgRating: string;
};

/** Wanneer de kopregels beginnen, en hoeveel later elke volgende. */
const LINE_DELAY_S = 0.08;
const LINE_STEP_S = 0.07;

/**
 * De opening (docs/VORMGEVING.md §3). Eén boodschap: salon én thuis, door één specialist.
 *
 * Computer: links de kop, de uitleg en één hoofdknop (het huidadvies); onderaan twee "deuren"
 * naar de salon en de webshop. Rechts het beeld tot de rand, met een onderschrift ín de foto.
 * Telefoon: het beeld bovenaan, daaronder kop, uitleg en knop. De deuren direct daaronder.
 *
 * Rustig, over Sarah: geen producten in de opening (die verkoopt de webshopdeur met een
 * concrete regel). Eén diepte-laag: het portret zakt trager weg dan de pagina en zoomt licht
 * uit (106% → 100%); het naambordje hoort bij de foto en beweegt mee. Op de computer vervaagt
 * de tekst als de hero half weg is. Alles stil bij "minder beweging"; op de telefoon de halve uitslag.
 */
export function HomeHero({
  reviewCount,
  avgRating
}: Props) {
  const pages = usePages();
  const ref = useRef<HTMLElement>(null);
  const {
    openQuiz
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const copy = pages.home.hero;
  // Standaard: Sarah met de producten die ze kiest (webshop eerst, de salon als bron). Te vervangen in Paginateksten.
  // Zonder eigen beeld: het sfeerbeeld met producten in de behandelkamer (docs/BEELDEN.md, H1).
  const media = mediaOr(copy.media, {
    image: placeholderImages.homeHero,
    alt: 'Een huidspecialist met producten in een lichte behandelkamer',
    focus: {
      x: 50,
      y: 30
    }
  });
  const press = pressLogos[0]?.name;
  const afterLines = LINE_DELAY_S + copy.lines.length * LINE_STEP_S;
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const portraitY = useLayerY(scrollYProgress, [0, 64]);
  const portraitScale = useLayerValue(scrollYProgress, [0, 1], [1.06, 1]);
  const textFade = useLayerValue(scrollYProgress, [0.45, 0.85], [1, 0]);
  // Vervagen alleen op de computer, waar de hero één scherm is. Op de telefoon staan kop, knop en
  // deuren onder het beeld: die lees je nog terwijl de hero wegscrolt.
  const compact = useCompactMotion();
  const fade = compact ? undefined : textFade;

  /** Zacht omhoog, na `delay` seconden. */
  const rise = (delay: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 8
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.26,
      delay,
      ease
    }
  };
  return <section ref={ref} className="bg-sand" aria-label="Introductie" data-id="element-7604" data-mp="3kde5jtymae7m">
      <div className="grid lg:min-h-[min(calc(100svh-120px),54rem)] lg:grid-cols-2" data-id="element-7605" data-mp="3kep6zn4lb9vk">
        {/* Beeld met zijn lagen: op de telefoon eerst, op de computer rechts tot de rand. */}
        <div className="relative lg:order-2" data-id="element-7606" data-mp="3al8h2u7kqex6">
          <div className="relative h-[40svh] min-h-[300px] max-h-[460px] overflow-hidden bg-blush sm:h-[52svh] sm:max-h-[560px] lg:h-full lg:max-h-none" data-id="element-7607" data-mp="35oi1e7zollca">
            {/* Extra hoogte boven: het beeld zakt trager weg dan de pagina, zonder ooit een rand te tonen. */}
            <motion.div style={portraitY && portraitScale ? {
            y: portraitY,
            scale: portraitScale
          } : undefined} className="absolute inset-x-0 -top-[12%] bottom-0 lg:-top-[9%]" data-id="element-7608" data-mp="25gci7qfhz91">
              <MediaView media={media} priority sizes="(min-width: 1024px) 50vw, 100vw" controlsPosition="top-right" className="h-full w-full" data-id="element-7609" data-mp="38wpz85jwsdgj" />
            </motion.div>

            {/* Het onderschrift ín de foto: op een shell-vlak, dus leesbaar op elk beeld. */}
            <div className="absolute bottom-4 left-4 max-w-[calc(100%-2rem)] sm:bottom-6 sm:left-6 lg:bottom-10 lg:left-10" data-id="element-7610" data-mp="5gj5orv7jwlh">
              <motion.div {...rise(0.25)} className="bg-shell px-4 py-3 sm:px-5 sm:py-4" data-id="element-7611" data-mp="3rvc0pljc86kz">
                <p data-id="element-7612" data-mp="3rthp4xtyhqtk">
                  <span className="block type-name text-ink" data-id="element-7613" data-mp="ord6bshrgsp">{founder.name}</span>
                  <span className="mt-0.5 block text-[13px] leading-snug text-muted" data-id="element-7614" data-mp="oelqsvkmx9e0">
                    {founder.role} · {policyCopy.experience}
                  </span>
                  {(reviewCount > 0 || press) && <span className="mt-2.5 hidden items-center gap-3 border-t border-line pt-2.5 text-[13px] text-ink sm:flex" data-id="element-7615" data-mp="2dv7mx7rq1q25">
                      {reviewCount > 0 && <span className="inline-flex items-center gap-1.5 whitespace-nowrap" data-id="element-7616" data-mp="2mo1cby0vu5tq">
                          <StarIcon className="h-3.5 w-3.5 fill-current text-clay" strokeWidth={0} aria-hidden="true" data-id="element-7617" data-mp="2imftzx5kwzc5" />
                          {avgRating}
                          <span className="sr-only" data-id="element-7618" data-mp="1uxesktv5yogd"> van 5 sterren,</span>
                          <span className="text-muted" data-id="element-7619" data-mp="1pyk0t2f7xpk4"> · {plural(reviewCount, 'review', 'reviews')}</span>
                        </span>}
                      {press && <span className="whitespace-nowrap text-muted" data-id="element-7620" data-mp="1yygigk36xo5o">Aanbevolen door {press}</span>}
                    </span>}
                </p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Tekst: de middenlaag. */}
        <motion.div style={fade ? {
        opacity: fade
      } : undefined} className="flex flex-col px-6 pb-12 pt-7 sm:px-10 sm:pt-10 lg:order-1 lg:py-14 lg:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))] lg:pr-24 xl:pr-28" data-id="element-7621" data-mp="3afq0d26o884v">
          <div className="lg:my-auto" data-id="element-7622" data-mp="2h0dxbiwis2rb">
            {/* De kop is de h1 (wat je hier kunt), het regeltje erboven zegt wie het zegt. */}
            <motion.p {...rise(0)} className="text-[14px] text-clay" data-id="element-7623" data-mp="2lzgqa4z86iix">
              {fill(copy.eyebrow)}
            </motion.p>
            <h1 className="mt-4 type-page text-ink lg:mt-5" data-id="element-7624" data-mp="1p7wmsjsulz1y">
              {copy.lines.map((line, i) => <span key={`${i}-${line.text}`} className="block overflow-hidden pb-[0.06em]" data-id="element-7625" data-mp="1yaeaz3s2d1rl" data-mpi={i}>
                  <motion.span className={`block ${line.accent ? 'italic text-clay' : ''}`} initial={reduce ? false : {
                y: '105%'
              }} animate={{
                y: 0
              }} transition={{
                duration: 0.3,
                delay: LINE_DELAY_S + i * LINE_STEP_S,
                ease
              }} data-id="element-7626" data-mp="1y8l53qp9ylkn">
                    {fill(line.text)}
                  </motion.span>
                </span>)}
            </h1>
            {copy.body && <motion.p {...rise(afterLines)} className="mt-5 max-w-[34rem] text-[17px] leading-relaxed text-muted lg:mt-6" data-id="element-7627" data-mp="y04v8sxr7n7p">
                {fill(copy.body)}
              </motion.p>}
            {/* Hoofdknop: kiezen op je huidvraag (het blok "Kiezen" verderop). De huidtest is de tweede weg. */}
            <motion.div {...rise(afterLines + 0.05)} className="mt-7 flex flex-col gap-3 sm:flex-row lg:mt-9" data-id="element-7628" data-mp="k49jd71rvcdt">
              <a href={`#${homeAnchor('concerns')}`} className={`${btnPrimaryLg} group w-full sm:w-auto`} data-id="element-7629" data-mp="acn0qpn3ub11">
                {pageActions.chooseByConcern}
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7630" data-mp="5f4zllfbx03l" />
              </a>
              {copy.cta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={`${btnSecondaryLg} w-full sm:w-auto`} data-id="element-7631" data-mp="3sj1m39npdn9g">
                  {fill(copy.cta)}
                </button>}
            </motion.div>
          </div>

          <motion.div {...rise(afterLines + 0.1)} className="mt-10 lg:mt-12" data-id="element-7632" data-mp="1b95bhx8npbb6">
            <HeroDoors data-id="element-7633" data-mp="1bb85escxjol6" />
          </motion.div>
        </motion.div>
      </div>
    </section>;
}